import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import theme from './theme';
import Layout from './components/Layout';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import CustomerManagement from './components/CustomerManagement';
import TransactionEntry from './components/TransactionEntry';
import PassbookPrint from './components/PassbookPrint';
import FirstPagePrintSettings from './components/PassbookFirstPageSettings';
import PassbookFirstPagePrint from './components/PassbookFirstPagePrint';
import PassbookTransactionSettings from './components/PassbookTransactionSettings';
import Profile from './components/Profile';
import AdminManagement from './components/AdminManagement';
import PermissionSettings from './components/PermissionSettings';
import ReportsDashboard from './components/ReportsDashboard';
import GasTransactionReport from './components/GasTransactionReport';
import CustomerReport from './components/CustomerReport';
import Unauthorized from './components/Unauthorized';
import './custom.css';

const AppContent = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
      } />
      <Route path="/register" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute requiredPermission="customers.view">
          <Layout>
            <CustomerManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/transactions" element={
        <ProtectedRoute requiredPermission="transactions.view">
          <Layout>
            <TransactionEntry />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/passbook" element={
        <ProtectedRoute requiredPermission="passbook.print">
          <Layout>
            <PassbookPrint />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/first-page-settings" element={
        <ProtectedRoute requiredPermission="passbook.print">
          <Layout>
            <FirstPagePrintSettings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/first-page-print" element={
        <ProtectedRoute requiredPermission="passbook.print">
          <Layout>
            <PassbookFirstPagePrint />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/transaction-settings" element={
        <ProtectedRoute requiredPermission="passbook.print">
          <Layout>
            <PassbookTransactionSettings />
          </Layout>
        </ProtectedRoute>
      } />
      {/* Redirect old route to new route */}
      <Route path="/passbook-first-page" element={<Navigate to="/first-page-settings" replace />} />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <Profile />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile/:mode" element={
        <ProtectedRoute>
          <Layout>
            <Profile />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin-management" element={
        <ProtectedRoute requiredPermission="system.manage">
          <Layout>
            <AdminManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/permission-settings" element={
        <ProtectedRoute requiredPermission="system.manage">
          <Layout>
            <PermissionSettings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute requiredPermission="transactions.view">
          <Layout>
            <ReportsDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/gas-transactions" element={
        <ProtectedRoute requiredPermission="transactions.view">
          <Layout>
            <GasTransactionReport />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/customers" element={
        <ProtectedRoute requiredPermission="customers.view">
          <Layout>
            <CustomerReport />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/" element={
        <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
      } />
    </Routes>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
